import { useEffect, useRef, useState } from 'react'
import { loadLut } from './look'
import { drawClean, setupLook, uploadLut, type LookGl } from './lookgl'
import type { Filmic } from './film/Filmic'

/*
  Draws a camera's video through its look on a WebGL 2 canvas laid over the <video>, which keeps
  playing underneath (clock, sound, buffering all stay on the element). A new frame is drawn only
  when the video has one (requestVideoFrameCallback), so a paused player costs nothing, and at the
  size it is shown (never above the file). Clean is the look's LUT; Filmic runs Film Emulation's grain
  and halation too (film/Filmic.ts, loaded on first use). If WebGL or the look fails, the canvas steps
  aside and the video shows as shot; if Filmic can't run here, the clean look stays.
*/
const FILMIC_MAX = 1280

type Gl = LookGl & { filmic?: Promise<Filmic | null> }

export function LookCanvas({ video, lut, film, onFail }: { video: HTMLVideoElement | null; lut: string; film: boolean; onFail: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const ctx = useRef<Gl | null>(null)
  const [ready, setReady] = useState(false)
  const failRef = useRef(onFail)
  failRef.current = onFail

  // One context per canvas; the LUT swaps in place.
  useEffect(() => {
    setReady(false)
    let live = true
    loadLut(lut)
      .then((bytes) => {
        if (!live || !ref.current) return
        ctx.current ??= setupLook(ref.current)
        const c = ctx.current
        if (!c) throw new Error('no webgl2')
        uploadLut(c, bytes)
        setReady(true)
      })
      .catch((e) => {
        console.warn('look off:', e)
        if (live) failRef.current()
      })
    return () => {
      live = false
    }
  }, [lut])

  // Filmic: the look's film data for this camera, loaded before the first filmic frame.
  const [filmic, setFilmic] = useState<Filmic | null>(null)
  useEffect(() => {
    setFilmic(null)
    const c = ctx.current
    if (!film || !ready || !c) return
    let live = true
    const [look, camera] = lut.split('/')
    c.filmic ??= import('./film/Filmic').then((m) => m.Filmic.create(c.gl)).catch((e) => (console.warn('filmic off:', e), null))
    c.filmic
      .then(async (f) => {
        if (!f) return
        await f.load(look, camera)
        if (live) setFilmic(f)
      })
      .catch((e) => console.warn('filmic off:', e))
    return () => {
      live = false
    }
  }, [film, ready, lut])

  useEffect(() => {
    const c = ctx.current
    if (!ready || !video || !c) return
    let live = true
    let vfc = 0
    let raf = 0
    const draw = (mediaTime?: number) => {
      if (!live || video.readyState < 2 || !video.videoWidth) return
      const { gl } = c
      const canvas = gl.canvas as HTMLCanvasElement
      // Shown size: the box the video fits in, times the screen's pixel density, never above the file.
      const vw = video.videoWidth, vh = video.videoHeight
      // Filmic stops at 1280 wide (the screen scales it up): its cost grows with the frame, and a full screen
      // CineStill at 1920 is three times the GPU time for a difference no one sees in a review.
      const fit = Math.min(1, filmic ? FILMIC_MAX / vw : 1, (Math.min(canvas.clientWidth / vw, canvas.clientHeight / vh) || 1) * (window.devicePixelRatio || 1))
      const w = Math.max(1, Math.round(vw * fit)), h = Math.max(1, Math.round(vh * fit))
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }
      try {
        if (filmic) {
          filmic.render(video, w, h, Math.round((mediaTime ?? video.currentTime) * 24))
          return
        }
        drawClean(c, video, w, h)
      } catch (e) {
        // A video without CORS can't be read: show it as shot.
        console.warn('look off:', e)
        live = false
        failRef.current()
      }
    }
    const hasVfc = 'requestVideoFrameCallback' in video
    const onFrame = (_: number, meta: VideoFrameCallbackMetadata) => {
      draw(meta.mediaTime)
      if (live) vfc = video.requestVideoFrameCallback(onFrame)
    }
    if (hasVfc) vfc = video.requestVideoFrameCallback(onFrame)
    else {
      const v = video as HTMLVideoElement // older Safari, no requestVideoFrameCallback
      const loop = () => {
        if (!v.paused) draw()
        raf = requestAnimationFrame(loop)
      }
      raf = requestAnimationFrame(loop)
    }
    // Paused moves (cue, seek, a new clip) land a frame without playing; a resize redraws at the new size.
    const redraw = () => draw()
    const events = ['loadeddata', 'seeked', 'canplay']
    events.forEach((e) => video.addEventListener(e, redraw))
    const ro = new ResizeObserver(redraw)
    ro.observe(c.gl.canvas as HTMLCanvasElement)
    draw()
    return () => {
      live = false
      if (hasVfc && vfc) video.cancelVideoFrameCallback(vfc)
      cancelAnimationFrame(raf)
      events.forEach((e) => video.removeEventListener(e, redraw))
      ro.disconnect()
    }
  }, [ready, video, filmic])

  return <canvas ref={ref} className="cam-look" style={{ visibility: ready ? 'visible' : 'hidden' }} />
}
